import React, { useEffect, Fragment, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useCookies } from 'react-cookie'
import { useIdleTimer } from 'react-idle-timer'
import { actionIsRecent, addAuditLog, checkCookieToken, detectBrowser, getSessionTimeout, updateAction } from './scripts/helpers';

import { Dialog, Transition } from '@headlessui/react'
import { ExclamationIcon } from '@heroicons/react/outline'

import { AuthProvider } from './AuthProvider';
import Protected from './Protected';
import RedirectIfLoggedIn from './RedirectIfLoggedIn';
import Loading from './components/shared/loading';

import './App.css';

// Eager load: Auth routes + main index
import Login from './routes/auth/login';
import ForgotPassword from './routes/auth/forgotPassword';
import ResetPassword from './routes/auth/resetPassword';
import Index from './routes/index';

// Code-split: Feature modules
const ComposeTwid = React.lazy(() => import('./routes/composeTwid'));

const UsersAdd = React.lazy(() => import('./routes/users/add'));
const UsersEdit = React.lazy(() => import('./routes/users/edit'));
const UsersIndex = React.lazy(() => import('./routes/users/index'));
const UsersView = React.lazy(() => import('./routes/users/view'));
const UsersSearch = React.lazy(() => import('./routes/users/search'));

const DropdownsAdd = React.lazy(() => import('./routes/dropdowns/add'));
const DropdownsEdit = React.lazy(() => import('./routes/dropdowns/edit'));
const DropdownsIndex = React.lazy(() => import('./routes/dropdowns/index'));
const DropdownsView = React.lazy(() => import('./routes/dropdowns/view'));

const ReferencesEdit = React.lazy(() => import('./routes/references/edit'));
const ReferencesIndex = React.lazy(() => import('./routes/references/index'));
const ReferencesView = React.lazy(() => import('./routes/references/view'));

const PrivilegesIndex = React.lazy(() => import('./routes/privileges/index'));
const PrivilegesEdit = React.lazy(() => import('./routes/privileges/edit'));

const PasswordsEdit = React.lazy(() => import('./routes/passwords/edit'));

const WorkersView2 = React.lazy(() => import('./routes/workers/view2'));
const ClientCalendar = React.lazy(() => import('./routes/workers/subsidiaries/clientCalendar'));

const ColumnVisibilityIndex = React.lazy(() => import('./routes/columnVisibilities/index'));
const ColumnVisibilityEdit = React.lazy(() => import('./routes/columnVisibilities/edit'));

const ProblemClansIndex = React.lazy(() => import('./routes/problemClans/index'));
const ProblemClansAdd = React.lazy(() => import('./routes/problemClans/add'));
const ProblemClansView = React.lazy(() => import('./routes/problemClans/view'));
const ProblemClansEdit = React.lazy(() => import('./routes/problemClans/edit'));

const DefaultValuesIndex = React.lazy(() => import('./routes/defaultValues/index'));
const DefaultValuesEdit = React.lazy(() => import('./routes/defaultValues/edit'));

const ProfilesIndex = React.lazy(() => import('./routes/profiles/index'));
const ProfilesEdit = React.lazy(() => import('./routes/profiles/edit'));

const DirectMessagesIndex = React.lazy(() => import('./routes/directMessages/index'));

const GroupChatsIndex = React.lazy(() => import('./routes/groupChats/index'));
const AllGroupChatsIndex = React.lazy(() => import('./routes/groupChats/all'));

const TemplatesIndex = React.lazy(() => import('./routes/templates'));
const TemplatesAdd = React.lazy(() => import('./routes/templates/add'));
const TemplatesView = React.lazy(() => import('./routes/templates/view'));
const TemplatesEdit = React.lazy(() => import('./routes/templates/edit'));

const FacepicAttachmentsIndex = React.lazy(() => import('./routes/facepicAttachments'));
const FacepicAttachmentsAdd = React.lazy(() => import('./routes/facepicAttachments/add'));
const FacepicAttachmentsView = React.lazy(() => import('./routes/facepicAttachments/view'));
const FacepicAttachmentsEdit = React.lazy(() => import('./routes/facepicAttachments/edit'));
const FacepicAttachmentsAssign = React.lazy(() => import('./routes/facepicAttachments/assign'));

const PdpaAttachmentsIndex = React.lazy(() => import('./routes/pdpaAttachments'));
const PdpaAttachmentsAdd = React.lazy(() => import('./routes/pdpaAttachments/add'));
const PdpaAttachmentsView = React.lazy(() => import('./routes/pdpaAttachments/view'));
const PdpaAttachmentsEdit = React.lazy(() => import('./routes/pdpaAttachments/edit'));
const PdpaAttachmentsAssign = React.lazy(() => import('./routes/pdpaAttachments/assign'));

const MyHistoryIndex = React.lazy(() => import('./routes/myHistory'));
const AuditLogIndex = React.lazy(() => import('./routes/auditLog'));

const AnnouncementsIndex = React.lazy(() => import('./routes/announcements'));
const AnnouncementsAdd = React.lazy(() => import('./routes/announcements/add'));
const AnnouncementsView = React.lazy(() => import('./routes/announcements/view'));
const AnnouncementsEdit = React.lazy(() => import('./routes/announcements/edit'));

const ExportIndex = React.lazy(() => import('./routes/export'));
const ImportIndex = React.lazy(() => import('./routes/import'));
const ImportImport = React.lazy(() => import('./routes/import/import'));
const ImportList = React.lazy(() => import('./routes/import/list'));
const ImportView = React.lazy(() => import('./routes/import/view'));

const SettingsEdit = React.lazy(() => import('./routes/settings/edit'));
const SettingsView = React.lazy(() => import('./routes/settings/view'));

const PresetReportsIndex = React.lazy(() => import('./routes/presetReports'));
const PresetReportsAdd = React.lazy(() => import('./routes/presetReports/add'));
const PresetReportsView = React.lazy(() => import('./routes/presetReports/view'));
const PresetReportsEdit = React.lazy(() => import('./routes/presetReports/edit'));

const PresetSkinsIndex = React.lazy(() => import('./routes/presetSkins'));
const PresetSkinsAdd = React.lazy(() => import('./routes/presetSkins/add'));
const PresetSkinsView = React.lazy(() => import('./routes/presetSkins/view'));
const PresetSkinsEdit = React.lazy(() => import('./routes/presetSkins/edit'));

const ReportsIndex = React.lazy(() => import('./routes/reports'));
const ReportsAdd = React.lazy(() => import('./routes/reports/add'));
const ReportsView = React.lazy(() => import('./routes/reports/view'));
const ReportsEdit = React.lazy(() => import('./routes/reports/edit'));

function App() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(['cookie']);
  const [sessionExpireDuration, setSessionExpireDuration] = useState();
  const [sessionPromptDuration, setSessionPromptDuration] = useState();
  const [promptModalDuration, setPromptModalDuration] = useState();

  useEffect(() => {
    if (cookies.token) {
      getSessionTimeout().then(result => {
        if (result.data.session_expire_duration !== null && cookies['role'] !== 'Admin') {
          setSessionExpireDuration(result.data.session_expire_duration * 60);
        }
        if (result.data.session_prompt_duration !== null && cookies['role'] !== 'Admin') {
          setPromptModalDuration(result.data.session_prompt_duration);
          setSessionPromptDuration(result.data.session_prompt_duration * 60);
        }

        if (checkCookieToken(document.cookie)) {
          actionIsRecent(cookies['token']).then((data) => {
            const recent = data.data.recent;

            if (!recent) {
              removeCookie('token');
              removeCookie('username');
              removeCookie('role');
              removeCookie('userId');
              removeCookie('announcementsSynced');
              window.location.replace("/login");
            }
          });
        }
      });
    }
  }, []);

  const onPrompt = () => {
    // Fire a Modal Prompt
    const whiteListedPaths = ['login', '/login', 'resetPassword', 'forgotPassword'];
    if (whiteListedPaths.indexOf(window.location.pathname) === -1 && cookies['role'] !== 'Admin') {
      setOpen(true);
      setPrompt(true);
    } else {
      reset();
    }
  }

  const onIdle = () => {
    // Close Modal Prompt
    // Do some idle action like log out your user
    if (prompt) {
      if (checkCookieToken(document.cookie)) {
        if (cookies['role'] !== 'Admin') {
          // check whether action is recent
          actionIsRecent(cookies['token']).then((data) => {
            const recent = data.data.recent;
            if (!recent) {
              const userId = cookies['userId'];
              setPrompt(false);

              addAuditLog({
                table_name: 'Auth',
                action_type: 'Session timeout',
                action_description: `From ${detectBrowser(navigator)}.`,
                user_id: userId
              }).then(result => {
                removeCookie('token');
                removeCookie('username');
                removeCookie('role');
                removeCookie('userId');
                removeCookie('announcementsSynced');
                window.location.replace("/login");
              });
            }
          }).catch(() => {
            window.location.replace("/login");
          })
        } else {
          window.location.replace("/login");
        }
      }
    }
  }

  const onActive = (event) => {
  }

  const onAction = (event) => {
    if (checkCookieToken(document.cookie)) {
      updateAction(cookies['token']).then(() => {

      }).catch(() => {
        window.location.replace("/login");
      });
    }
  }

  const {
    start,
    reset,
    activate,
    pause,
    resume,
    isIdle,
    isPrompted,
    isLeader,
    getTabId,
    getRemainingTime,
    getElapsedTime,
    getLastIdleTime,
    getLastActiveTime,
    getTotalIdleTime,
    getTotalActiveTime
  } = useIdleTimer({
    onPrompt,
    onIdle,
    onActive,
    onAction,
    timeout: 1000 * sessionExpireDuration,
    promptTimeout: 1000 * sessionPromptDuration,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange'
    ],
    immediateEvents: [],
    debounce: 0,
    throttle: 0,
    eventsThrottle: 30000,
    // eventsThrottle: 1000,
    element: document,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: false,
    name: 'idle-timer',
    syncTimers: 0,
    leaderElection: false
  });

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500">
                      <ExclamationIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Session Timeout
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You will be logged out if inactive for the next {promptModalDuration} minutes.
                        </p>
                      </div>
                      <button className="inline-flex items-center grow-2 mt-5 px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 text-white bg-red-500 hover:bg-red-600" onClick={() => {
                        setOpen(false);
                        setPrompt(false);
                        reset();
                      }}>Close</button>
                    </div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
            <Routes>
            <Route path='/search-users' element={<Protected><UsersSearch /></Protected>} />

            <Route path='/profile' element={<Protected><ProfilesIndex /></Protected>} />
            <Route path='/profile/edit' element={<Protected><ProfilesEdit /></Protected>} />
            <Route path='/change-password' element={<Protected><PasswordsEdit /></Protected>} />

            <Route path='/login' element={<RedirectIfLoggedIn><Login /></RedirectIfLoggedIn>} />
            <Route path='/forgotPassword' element={<RedirectIfLoggedIn><ForgotPassword /></RedirectIfLoggedIn>} />
            <Route path='/resetPassword' element={<RedirectIfLoggedIn><ResetPassword /></RedirectIfLoggedIn>} />

            <Route path='/compose-twid' element={<Protected><ComposeTwid /></Protected>} />
            <Route path='/my-history' element={<Protected><MyHistoryIndex /></Protected>} />
            <Route path='/audit-log' element={<Protected><AuditLogIndex /></Protected>} />

            <Route path='/preset-reports' element={<Protected><PresetReportsIndex /></Protected>} />
            <Route path='/preset-skins/:id/generate-preset-report' element={<Protected><PresetReportsAdd /></Protected>} />
            <Route path='/preset-reports/:id' element={<Protected><PresetReportsView /></Protected>} />
            <Route path='/preset-reports/:id/edit' element={<Protected><PresetReportsEdit /></Protected>} />

            <Route path='/preset-skins' element={<Protected><PresetSkinsIndex /></Protected>} />
            <Route path='/preset-skins/add' element={<Protected><PresetSkinsAdd /></Protected>} />
            <Route path='/preset-skins/:id' element={<Protected><PresetSkinsView /></Protected>} />
            <Route path='/preset-skins/:id/edit' element={<Protected><PresetSkinsEdit /></Protected>} />

            <Route path='/reports' element={<Protected><ReportsIndex /></Protected>} />
            <Route path='/reports/add' element={<Protected><ReportsAdd /></Protected>} />
            <Route path='/reports/:id' element={<Protected><ReportsView /></Protected>} />
            <Route path='/reports/:id/edit' element={<Protected><ReportsEdit /></Protected>} />

            <Route path='/settings/edit' element={<Protected><SettingsEdit /></Protected>} />
            <Route path='/settings' element={<Protected><SettingsView /></Protected>} />

            <Route path='/direct-messages' element={<Protected><DirectMessagesIndex /></Protected>} />
            <Route path='/direct-messages/:username' element={<Protected><DirectMessagesIndex /></Protected>} />

            <Route path='/group-chats' element={<Protected><GroupChatsIndex /></Protected>} />
            <Route path='/all-group-chats' element={<Protected><AllGroupChatsIndex /></Protected>} />
            <Route path='/group-chats/:groupChatSlug' element={<Protected><GroupChatsIndex /></Protected>} />

            <Route path='/problem-clans' element={<Protected><ProblemClansIndex /></Protected>} />
            <Route path='/problem-clans/add' element={<Protected><ProblemClansAdd /></Protected>} />
            <Route path='/problem-clans/:id' element={<Protected><ProblemClansView /></Protected>} />
            <Route path='/problem-clans/:id/edit' element={<Protected><ProblemClansEdit /></Protected>} />

            <Route path='/column-visibilities' element={<Protected><ColumnVisibilityIndex /></Protected>} />
            <Route path='/column-visibilities/:tableSlug/edit' element={<Protected><ColumnVisibilityEdit /></Protected>} />

            <Route path='/' element={<Protected><Index /></Protected>} />
            <Route path='/users/add' element={<Protected><UsersAdd /></Protected>} />
            <Route path='/users/:id/edit' element={<Protected><UsersEdit /></Protected>} />
            <Route path='/users' element={<Protected><UsersIndex /></Protected>} />
            <Route path='/users/:id' element={<Protected><UsersView /></Protected>} />

            <Route path='/announcements' element={<Protected><AnnouncementsIndex /></Protected>} />
            <Route path='/announcements/add' element={<Protected><AnnouncementsAdd /></Protected>} />
            <Route path='/announcements/:id' element={<Protected><AnnouncementsView /></Protected>} />
            <Route path='/announcements/:id/edit' element={<Protected><AnnouncementsEdit /></Protected>} />

            <Route path='/templates' element={<Protected><TemplatesIndex /></Protected>} />
            <Route path='/templates/add' element={<Protected><TemplatesAdd /></Protected>} />
            <Route path='/templates/:id' element={<Protected><TemplatesView /></Protected>} />
            <Route path='/templates/:id/edit' element={<Protected><TemplatesEdit /></Protected>} />

            <Route path='/pdpa-attachments' element={<Protected><PdpaAttachmentsIndex /></Protected>} />
            <Route path='/pdpa-attachments/add' element={<Protected><PdpaAttachmentsAdd /></Protected>} />
            <Route path='/pdpa-attachments/:id' element={<Protected><PdpaAttachmentsView /></Protected>} />
            <Route path='/pdpa-attachments/:id/edit' element={<Protected><PdpaAttachmentsEdit /></Protected>} />
            <Route path='/pdpa-attachments/:id/assign' element={<Protected><PdpaAttachmentsAssign /></Protected>} />

            <Route path='/dropdowns' element={<Protected><DropdownsIndex /></Protected>} />
            <Route path='/dropdowns/add' element={<Protected><DropdownsAdd /></Protected>} />
            <Route path='/dropdowns/:id/edit' element={<Protected><DropdownsEdit /></Protected>} />
            <Route path='/dropdowns/:id' element={<Protected><DropdownsView /></Protected>} />

            <Route path='/references' element={<Protected><ReferencesIndex /></Protected>} />
            <Route path='/references/:id/edit' element={<Protected><ReferencesEdit /></Protected>} />
            <Route path='/references/:id' element={<Protected><ReferencesView /></Protected>} />

            <Route path='/export' element={<Protected><ExportIndex /></Protected>} />
            <Route path='/imports' element={<Protected><ImportIndex /></Protected>} />
            <Route path='/imports/:tableSlug' element={<Protected><ImportList /></Protected>} />
            <Route path='/imports/:tableSlug/import' element={<Protected><ImportImport /></Protected>} />
            <Route path='/imports/:tableSlug/:id' element={<Protected><ImportView /></Protected>} />

            <Route path='/privileges' element={<Protected><PrivilegesIndex /></Protected>} />
            <Route path='/privileges/:tableSlug/edit' element={<Protected><PrivilegesEdit /></Protected>} />

            <Route path='/default-values' element={<Protected><DefaultValuesIndex /></Protected>} />
            <Route path='/default-values/:defaultTableSlug/edit' element={<Protected><DefaultValuesEdit /></Protected>} />

            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/calendar-view' element={<Protected><ClientCalendar /></Protected>} />
            <Route path='/workers/:workerId' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction/:jobSubsidiaryIdOrJobSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction/:jobSubsidiaryIdOrJobSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction/:jobSubsidiaryIdOrJobSubsidiaryAction/:problemSubsidiarySlugOrJobSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction/:jobSubsidiaryIdOrJobSubsidiaryAction/:problemSubsidiarySlugOrJobSubsidiaryAction/:problemSubsidiaryIdOrProblemSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
            <Route path='/workers/:workerId/:workerSubsidiarySlugOrWorkerAction/:workerSubsidiaryIdOrWorkerSubsidiaryAction/:jobSubsidiarySlugOrWorkerSubsidiaryAction/:jobSubsidiaryIdOrJobSubsidiaryAction/:problemSubsidiarySlugOrJobSubsidiaryAction/:problemSubsidiaryIdOrProblemSubsidiaryAction/:problemSubsidiaryAction' element={<Protected><WorkersView2 /></Protected>} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
